import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Users, BookOpen, ClipboardCheck, Copy } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InstructorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cohorts');
  const [cohorts, setCohorts] = useState([]);
  const [showCreateCohort, setShowCreateCohort] = useState(false);
  const [newCohort, setNewCohort] = useState({ name: '' });
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [cases, setCases] = useState([]);
  const [sims, setSims] = useState([]);
  const [showCreateAssignment, setShowCreateAssignment] = useState(false);
  const [newAssignment, setNewAssignment] = useState({
    content_type: 'case',
    case_study_id: '',
    simulation_id: '',
    due_date: '',
    instructions: ''
  });
  const [progressData, setProgressData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  useEffect(() => {
    fetchCohorts();
    fetchCases();
    fetchSims();
  }, []);

  const fetchSims = async () => {
    try {
      const res = await axios.get(`${API}/simulations`);
      setSims(res.data || []);
    } catch { /* ignore */ }
  };

  const fetchCohortProgress = async (cohort) => {
    setLoadingProgress(true);
    try {
      const res = await axios.get(`${API}/instructor/cohorts/${cohort.id}/progress`);
      setProgressData(res.data);
    } catch {
      toast.error('Failed to load progress');
    } finally {
      setLoadingProgress(false);
    }
  };

  useEffect(() => {
    fetchCohorts();
    fetchCases();
  }, []);

  const fetchCohorts = async () => {
    try {
      const response = await axios.get(`${API}/instructor/cohorts`);
      setCohorts(response.data);
    } catch (error) {
      toast.error('Failed to load classes');
    }
  };

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases`);
      setCases(response.data);
    } catch (error) {
      console.error('Failed to load cases');
    }
  };

  const handleCreateCohort = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/instructor/cohorts`, newCohort);
      toast.success('Class created successfully');
      setShowCreateCohort(false);
      setNewCohort({ name: '' });
      fetchCohorts();
    } catch (error) {
      toast.error('Failed to create class');
    }
  };

  const copyJoinCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Join code copied to clipboard');
  };

  const viewCohortDetails = async (cohort) => {
    setSelectedCohort(cohort);
    try {
      const response = await axios.get(`${API}/instructor/assignments/${cohort.id}`);
      setAssignments(response.data);
      setActiveTab('assignments');
    } catch (error) {
      toast.error('Failed to load assignments');
    }
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    try {
      const body = {
        content_type: newAssignment.content_type,
        cohort_id: selectedCohort.id,
        due_date: newAssignment.due_date,
        instructions: newAssignment.instructions,
        case_study_id: newAssignment.content_type === 'case' ? newAssignment.case_study_id : '',
        simulation_id: newAssignment.content_type === 'simulation' ? newAssignment.simulation_id : '',
      };
      await axios.post(`${API}/instructor/assignments`, body);
      toast.success('Assignment created successfully');
      setShowCreateAssignment(false);
      setNewAssignment({ content_type: 'case', case_study_id: '', simulation_id: '', due_date: '', instructions: '' });
      viewCohortDetails(selectedCohort);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const viewSubmissions = (assignmentId) => {
    navigate(`/instructor/submissions/${assignmentId}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="fixed top-0 w-full z-50 glassmorphism">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-serif font-bold tracking-tight text-slate-900" data-testid="logo-link">
              The Case Verse - Instructor
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/cases" className="text-slate-700 hover:text-slate-900">Case Library</Link>
              <Link to="/book-training" className="text-slate-700 hover:text-slate-900" data-testid="nav-book-training-link">Book Training</Link>
              <Link to="/request-case" className="text-slate-700 hover:text-slate-900" data-testid="nav-request-case-link">Request Case</Link>
              <button onClick={logout} className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-serif font-bold tracking-tight text-slate-900 mb-8" data-testid="page-title">
            Instructor Dashboard
          </h1>

          <div className="border-b border-slate-200 mb-8">
            <div className="flex gap-8">
              <button
                onClick={() => { setActiveTab('cohorts'); setSelectedCohort(null); }}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'cohorts' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-cohorts"
              >
                My Classes
              </button>
              {selectedCohort && (
                <button
                  onClick={() => setActiveTab('assignments')}
                  className={`pb-4 border-b-2 transition-colors ${
                    activeTab === 'assignments' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                  }`}
                  data-testid="tab-assignments"
                >
                  {selectedCohort.name} - Assignments
                </button>
              )}
            </div>
          </div>

          {activeTab === 'cohorts' && (
            <div data-testid="cohorts-section">
              <div className="mb-6">
                <button
                  onClick={() => setShowCreateCohort(true)}
                  className="px-6 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all flex items-center gap-2"
                  data-testid="create-cohort-button"
                >
                  <Plus className="w-5 h-5" />
                  Create New Class
                </button>
              </div>

              {showCreateCohort && (
                <div className="bg-white p-6 rounded border border-slate-200 mb-6">
                  <h3 className="text-xl font-semibold mb-4">Create New Class</h3>
                  <form onSubmit={handleCreateCohort} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Class Name *</label>
                      <input
                        type="text"
                        required
                        value={newCohort.name}
                        onChange={(e) => setNewCohort({ name: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="e.g., MBA Finance 2026 Batch A"
                        data-testid="cohort-name-input"
                      />
                    </div>
                    <div className="flex gap-4">
                      <button type="submit" className="px-6 py-2 bg-slate-900 text-white rounded hover:bg-slate-800">
                        Create Class
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCreateCohort(false)}
                        className="px-6 py-2 border border-slate-300 rounded hover:border-slate-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="grid grid-cols-3 gap-6">
                {cohorts.map((cohort) => (
                  <div
                    key={cohort.id}
                    className="bg-white p-6 rounded border border-slate-200 hover:border-slate-900 transition-all cursor-pointer"
                    onClick={() => viewCohortDetails(cohort)}
                    data-testid={`cohort-card-${cohort.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <Users className="w-10 h-10 text-orange-600" />
                      <span className={`px-2 py-1 rounded text-xs ${
                        cohort.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {cohort.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <h3 className="text-xl font-serif font-semibold mb-2">{cohort.name}</h3>
                    <div className="text-sm text-slate-600 mb-4">
                      <div>Students: {cohort.student_count || 0}</div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-slate-600">Join Code:</span>
                      <code className="bg-slate-100 px-2 py-1 rounded font-mono">{cohort.join_code}</code>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyJoinCode(cohort.join_code); }}
                        className="p-1 hover:bg-slate-100 rounded"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'assignments' && selectedCohort && (
            <div data-testid="assignments-section">
              <div className="mb-6 flex items-center justify-between">
                <button
                  onClick={() => { setActiveTab('cohorts'); setSelectedCohort(null); }}
                  className="text-slate-600 hover:text-slate-900"
                >
                  ← Back to Classes
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchCohortProgress(selectedCohort)}
                    className="px-4 py-3 border border-slate-300 rounded hover:bg-slate-50 transition-all flex items-center gap-2 text-sm"
                    data-testid="view-progress-button"
                  >
                    <ClipboardCheck className="w-4 h-4" /> View Progress
                  </button>
                  <button
                    onClick={() => setShowCreateAssignment(true)}
                    className="px-6 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all flex items-center gap-2"
                    data-testid="create-assignment-button"
                  >
                    <Plus className="w-5 h-5" />
                    Create Assignment
                  </button>
                </div>
              </div>

              {progressData && (
                <div className="bg-white p-6 rounded border border-slate-200 mb-6" data-testid="progress-panel">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-serif font-semibold text-slate-900">Cohort Progress — {progressData.cohort.name}</h3>
                      <p className="text-sm text-slate-500">{progressData.cohort.student_count} students · {progressData.assignments.length} assignments</p>
                    </div>
                    <button onClick={() => setProgressData(null)} className="text-slate-500 hover:text-slate-900 text-sm">Close</button>
                  </div>
                  {loadingProgress ? (
                    <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>
                  ) : progressData.assignments.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-6">No assignments yet — create one to start tracking progress.</p>
                  ) : (
                    <div className="space-y-4">
                      {progressData.assignments.map(a => (
                        <div key={a.assignment_id} className="border border-slate-200 rounded-lg p-4" data-testid={`progress-assignment-${a.assignment_id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="text-xs uppercase font-mono text-slate-500">{a.content_type === 'simulation' ? '🎮 Simulation' : '📄 Case'}</div>
                              <div className="font-medium text-slate-900">{a.title}</div>
                              {a.due_date && <div className="text-xs text-slate-500">Due: {new Date(a.due_date).toLocaleDateString()}</div>}
                            </div>
                            <div className="text-right text-xs text-slate-600">
                              <div><strong className="text-slate-900">{a.aggregate.completed}</strong>/{a.aggregate.total_students} completed</div>
                              <div>{a.aggregate.started} started</div>
                              {a.aggregate.avg_score !== null && <div>Avg score: <strong className="text-orange-600">{a.aggregate.avg_score}</strong></div>}
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="text-slate-500">
                                <tr><th className="text-left py-1">Student</th><th className="text-left py-1">Status</th><th className="text-left py-1">Score</th><th className="text-left py-1">Submitted</th></tr>
                              </thead>
                              <tbody>
                                {a.student_stats.map(s => (
                                  <tr key={s.student_id} className="border-t border-slate-100">
                                    <td className="py-1.5 text-slate-800">{s.student_name || s.student_email}</td>
                                    <td className="py-1.5">
                                      {s.status === 'completed' && <span className="text-green-700 bg-green-50 px-2 py-0.5 rounded text-xs">✅ Completed</span>}
                                      {s.status === 'in_progress' && <span className="text-blue-700 bg-blue-50 px-2 py-0.5 rounded text-xs">⏳ In progress</span>}
                                      {s.status === 'not_started' && <span className="text-slate-500 bg-slate-50 px-2 py-0.5 rounded text-xs">Not started</span>}
                                    </td>
                                    <td className="py-1.5 text-slate-700">{s.score !== null ? s.score : '—'}</td>
                                    <td className="py-1.5 text-slate-500">{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {showCreateAssignment && (
                <div className="bg-white p-6 rounded border border-slate-200 mb-6">
                  <h3 className="text-xl font-semibold mb-4">Create New Assignment</h3>
                  <form onSubmit={handleCreateAssignment} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Assignment Type *</label>
                      <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-lg w-fit">
                        <button type="button" onClick={() => setNewAssignment({ ...newAssignment, content_type: 'case' })} className={`px-4 py-1.5 rounded-md text-sm font-medium ${newAssignment.content_type === 'case' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`} data-testid="assign-type-case">Case Study</button>
                        <button type="button" onClick={() => setNewAssignment({ ...newAssignment, content_type: 'simulation' })} className={`px-4 py-1.5 rounded-md text-sm font-medium ${newAssignment.content_type === 'simulation' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`} data-testid="assign-type-simulation">Simulation</button>
                      </div>
                    </div>
                    {newAssignment.content_type === 'case' ? (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Select Case Study *</label>
                        <select
                          required
                          value={newAssignment.case_study_id}
                          onChange={(e) => setNewAssignment({ ...newAssignment, case_study_id: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="case-select"
                        >
                          <option value="">-- Select Case --</option>
                          {cases.map((caseItem) => (
                            <option key={caseItem.id} value={caseItem.id}>
                              {caseItem.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Select Simulation *</label>
                        <select
                          required
                          value={newAssignment.simulation_id}
                          onChange={(e) => setNewAssignment({ ...newAssignment, simulation_id: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                          data-testid="simulation-select"
                        >
                          <option value="">-- Select Simulation --</option>
                          {sims.map((s) => (
                            <option key={s.id} value={s.id}>{s.title}{s.subject ? ` — ${s.subject}` : ''}</option>
                          ))}
                        </select>
                        {sims.length === 0 && <p className="text-xs text-slate-500 mt-1">No published simulations yet. Publish one from Admin → Simulations first.</p>}
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Due Date (Optional)</label>
                      <input
                        type="date"
                        value={newAssignment.due_date}
                        onChange={(e) => setNewAssignment({ ...newAssignment, due_date: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                        data-testid="due-date-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Instructions (Optional)</label>
                      <textarea
                        value={newAssignment.instructions}
                        onChange={(e) => setNewAssignment({ ...newAssignment, instructions: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                        rows="3"
                        placeholder="Any specific instructions for students..."
                        data-testid="instructions-input"
                      />
                    </div>
                    <div className="flex gap-4">
                      <button type="submit" className="px-6 py-2 bg-slate-900 text-white rounded hover:bg-slate-800">
                        Create Assignment
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCreateAssignment(false)}
                        className="px-6 py-2 border border-slate-300 rounded hover:border-slate-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="space-y-4">
                {assignments.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded border border-slate-200">
                    <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600">No assignments yet. Create your first assignment!</p>
                  </div>
                ) : (
                  assignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      className="bg-white p-6 rounded border border-slate-200 hover:border-slate-900 transition-all"
                      data-testid={`assignment-card-${assignment.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="text-xl font-serif font-semibold mb-2">{assignment.case_title}</h3>
                          {assignment.due_date && (
                            <p className="text-sm text-slate-600 mb-2">
                              Due: {new Date(assignment.due_date).toLocaleDateString()}
                            </p>
                          )}
                          {assignment.instructions && (
                            <p className="text-sm text-slate-600 mb-4">{assignment.instructions}</p>
                          )}
                          <p className="text-xs text-slate-500">
                            Created: {new Date(assignment.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={() => viewSubmissions(assignment.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800 transition-all"
                          data-testid={`view-submissions-${assignment.id}`}
                        >
                          <ClipboardCheck className="w-4 h-4" />
                          View Submissions
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
